import type { ReactNode, RefObject } from 'react';
import { twMerge } from 'tailwind-merge';
import { Popover } from '@acast-tech/decibel';

export interface CustomTooltipProps {
  children: ReactNode;
  className?: string;
  isOpen: boolean;
  popoverRef: RefObject<HTMLDivElement>;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  zIndex?: number;
}

export interface CustomTooltipTriggerProps {
  children: ReactNode;
  setIsOpen: (isOpen: boolean) => void;
  popoverRef: RefObject<HTMLDivElement>;
  className?: string;
  ariaExpanded?: boolean;
  ariaLabel?: string;
}

/**
 * This is the custom tooltip component.
 *
 * @param children - The children of the tooltip.
 * @param className - The class name of the tooltip.
 * @param isOpen - Whether the tooltip is open.
 * @param popoverRef - The popover ref.
 * @param zIndex - The z-index of the tooltip.
 * @param placement - The placement of the tooltip.
 */
const CustomTooltip = ({
  children,
  className,
  isOpen,
  popoverRef,
  zIndex,
  placement,
}: CustomTooltipProps) => {
  return (
    isOpen && (
      <Popover
        data-testid="custom-tooltip-popover"
        targetRef={popoverRef}
        placement={placement ?? 'top'}
        style={{
          zIndex: zIndex ?? 0,
          left: '0',
        }}
      >
        <div
          data-testid="custom-tooltip"
          className={twMerge(
            'absolute bg-textDarkGrey/75 text-white rounded-sm min-w-[250px] whitespace-normal text-xs p-2',
            className
          )}
        >
          {children}
        </div>
      </Popover>
    )
  );
};

/**
 * This is the custom tooltip trigger component.
 *
 * @param children - The children of the trigger.
 * @param setIsOpen - The function to set the tooltip open state.
 * @param popoverRef - The popover ref.
 * @param className - The class name of the trigger.
 */
export const CustomTooltipTrigger = ({
  children,
  setIsOpen,
  popoverRef,
  className,
  ariaExpanded,
  ariaLabel,
}: CustomTooltipTriggerProps) => {
  return (
    <div
      className={className}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      ref={popoverRef}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );
};

export default CustomTooltip;
